import React, { useEffect, useState } from 'react';
import { Steps, Button, Typography, Select, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { RouteComponentProps } from 'react-router-dom';
import { getEcole } from '../ecole/ecole.reducer';
import { PRIMARY_COLOR } from './upload.constants';

import OperationSelection from './components/OperationSelection';
import TemplateDownload from './components/TemplateDownload';
import FileUpload from './components/FileUpload';
import FileProcessing from './components/FileProcessing';
import ValidationSummary from './components/ValidationSummary';

import { useFileProcessing } from './hooks/useFileProcessing';
import { useValidation } from './hooks/useValidation';

import './upload-wizard.scss';
import { UploadType } from './upload.types';
import { uploadEleveCreation, uploadEleveInscription, uploadEleveReinscription, uploadEleveUpdate } from './uploads.reducer';
import { getEcoleAnneeScolaire } from '../annee-scolaire/annee-scolaire.reducer';
import { StatusAnneeScolaire } from 'app/shared/model/anneeScolaire.model';

const { Title } = Typography;

const UploadWizard = (props: RouteComponentProps<{ ecoleid: string }>) => {
  const [current, setCurrent] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAnneeScolaire, setSelectedAnneeScolaire] = useState(null);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: string } | null>(null);
  const [editedData, setEditedData] = useState<any[]>([]);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const dispatch = useAppDispatch();
  const { entities: anneeScolaires, entityActive: activeAnnee } = useAppSelector(state => state.anneeScolaire);
  const { isParsing, parsingProgress, parsedData, parsedHeaders, parseExcelFile, exportToExcel, setParsedData, generateCorrectedExcel } =
    useFileProcessing();

  const { errors, errorMap, validateData } = useValidation(props.match.params.ecoleid);

  useEffect(() => {
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(getEcoleAnneeScolaire(props.match.params.ecoleid));
  }, []);

  useEffect(() => {
    if (parsedData.length > 0) {
      setEditedData([...parsedData]);
      validateData(parsedData, parsedHeaders, selectedOperation);
    }
  }, [parsedData, parsedHeaders, selectedOperation]);

  const handleCellEdit = (rowIndex: number, field: string, value: string): void => {
    const newData = [...editedData];
    newData[rowIndex][field] = value;
    setEditedData(newData);
    validateData(newData, parsedHeaders, selectedOperation);
    const correctedFile = generateCorrectedExcel(editedData, parsedHeaders, errorMap, selectedOperation);
    setSelectedFile(correctedFile);
  };

  const handleConfirm = () => {
    if (!selectedFile || !selectedOperation) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    const entity = { ecoleId: props.match.params.ecoleid, file: formData };
    const entityAnnee = {
      ecoleId: props.match.params.ecoleid,
      anneeScolaireId: selectedAnneeScolaire,
      file: formData,
    };

    switch (selectedOperation.label) {
      case UploadType.CREATION:
        dispatch(uploadEleveCreation(entity)).then(() => {
          setIsConfirmModalVisible(false);
          setSelectedFile(null);
        });
        break;
      case UploadType.MISE_A_JOUR:
        dispatch(uploadEleveUpdate(entity)).then(() => {
          setIsConfirmModalVisible(false);
          setSelectedFile(null);
        });
        break;
      case UploadType.INSCRIPTION:
        dispatch(uploadEleveInscription(entityAnnee)).then(() => {
          setIsConfirmModalVisible(false);
          setSelectedFile(null);
        });
        break;
      case UploadType.REINSCRIPTION:
        dispatch(uploadEleveReinscription(entityAnnee)).then(() => {
          setIsConfirmModalVisible(false);
          setSelectedFile(null);
        });
        break;
      default:
        break;
    }
  };

  const getSteps = () => {
    const baseSteps = [
      {
        title: "Sélection de l'opération",
        content: <OperationSelection selectedOperation={selectedOperation} setSelectedOperation={setSelectedOperation} />,
      },
    ];

    // Add year selection step for inscription and reinscription before template download
    if (selectedOperation && (selectedOperation.label === UploadType.INSCRIPTION || selectedOperation.label === UploadType.REINSCRIPTION)) {
      baseSteps.push({
        title: "Sélection de l'année scolaire",
        content: (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Select
              style={{ width: '100%', maxWidth: '400px' }}
              placeholder="Sélectionnez une année scolaire"
              value={selectedAnneeScolaire}
              onChange={setSelectedAnneeScolaire}
              options={anneeScolaires
                .filter(annee => annee.statutAnneeScolaire !== StatusAnneeScolaire.CLOTUREE)
                .map(annee => ({
                  value: annee.id,
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{annee.nom}</span>
                      <Tag
                        color={annee.statutAnneeScolaire === StatusAnneeScolaire.ACTIVE ? 'green' : 'red'}
                        style={{ borderRadius: '10', padding: '0 10px' }}
                      >
                        {annee.statutAnneeScolaire === StatusAnneeScolaire.ACTIVE ? 'Active' : 'Inactive'}
                      </Tag>
                    </div>
                  ),
                }))}
            />
          </div>
        ),
      });
    }

    baseSteps.push(
      {
        title: 'Téléchargement du Template',
        content: (
          <TemplateDownload
            selectedOperation={selectedOperation}
            ecoleId={props.match.params.ecoleid}
            anneeScolaireId={activeAnnee.id || selectedAnneeScolaire}
          />
        ),
      },
      {
        title: 'Dépôt du Fichier',
        content: <FileUpload selectedFile={selectedFile} setSelectedFile={setSelectedFile} parseExcelFile={parseExcelFile} />,
      },
      {
        title: 'Traitement du Fichier',
        content: (
          <FileProcessing
            isParsing={isParsing}
            parsingProgress={parsingProgress}
            parsedData={editedData}
            parsedHeaders={parsedHeaders}
            selectedOperation={selectedOperation}
            errors={errors}
            errorMap={errorMap}
            editingCell={editingCell}
            setEditingCell={setEditingCell}
            handleCellEdit={handleCellEdit}
            exportToExcel={() => exportToExcel(editedData, parsedHeaders, errorMap, selectedOperation)}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        ),
      },
      {
        title: 'Validation',
        content: (
          <ValidationSummary
            selectedOperation={selectedOperation}
            parsedData={editedData}
            errors={errors}
            isConfirmModalVisible={isConfirmModalVisible}
            setIsConfirmModalVisible={setIsConfirmModalVisible}
            handleConfirm={handleConfirm}
            selectedFile={selectedFile}
          />
        ),
      }
    );

    return baseSteps;
  };

  const steps = getSteps();

  return (
    <div className="upload-wizard-container">
      <div className="upload-card">
        <Title level={2} style={{ marginBottom: 32, textAlign: 'center', color: PRIMARY_COLOR }}>
          Assistant d&apos;importation de données
        </Title>
        <div className="custom-steps-wrapper">
          <Steps current={current} className="custom-steps">
            {steps.map((item, index) => (
              <Steps.Step
                key={item.title}
                title={item.title}
                className="p-2"
                icon={
                  <div className={`step-number ${current === index ? 'active' : ''} ${current > index ? 'completed' : ''}`}>
                    {index + 1}
                  </div>
                }
              />
            ))}
          </Steps>
        </div>
        <div className="steps-content" style={{ marginBottom: 32, minHeight: 400 }}>
          {steps[current].content}
        </div>
        <div className="steps-action" style={{ textAlign: 'center' }}>
          {current > 0 && (
            <Button
              style={{
                margin: '0 8px',
                borderColor: PRIMARY_COLOR,
                color: PRIMARY_COLOR,
                height: '40px',
                padding: '0 24px',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
              }}
              onClick={() => setCurrent(current - 1)}
            >
              Précédent
            </Button>
          )}
          {current < steps.length - 1 && (
            <Button
              type="primary"
              onClick={() => setCurrent(current + 1)}
              disabled={
                (current === 0 && !selectedOperation) ||
                (selectedOperation &&
                  (selectedOperation.label === UploadType.INSCRIPTION || selectedOperation.label === UploadType.REINSCRIPTION) &&
                  current === 1 &&
                  !selectedAnneeScolaire) ||
                (selectedOperation &&
                  (selectedOperation.label === UploadType.CREATION || selectedOperation.label === UploadType.MISE_A_JOUR) &&
                  current === 2 &&
                  !selectedFile) ||
                (selectedOperation &&
                  (selectedOperation.label === UploadType.INSCRIPTION || selectedOperation.label === UploadType.REINSCRIPTION) &&
                  current === 3 &&
                  !selectedFile) ||
                (current === 4 && errors.length > 0)
              }
              style={{
                backgroundColor: PRIMARY_COLOR,
                borderColor: PRIMARY_COLOR,
                boxShadow: 'none',
                color: 'white',
                height: '40px',
                padding: '0 24px',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
              }}
            >
              Suivant
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadWizard;
